import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function GestionBus() {
    const [bus, setBus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        nom: '',
        chauffeur_nom: '',
        chauffeur_prenom: '',
        chauffeur_telephone: '',
        photo: ''
    });
    const [photoPreview, setPhotoPreview] = useState(null);
    const [programmeData, setProgrammeData] = useState(null);
    const [busProgramme, setBusProgramme] = useState(null);
    const [besoinHuile, setBesoinHuile] = useState(false);
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    
    const fileInputRef = useRef(null);
    
    const API_BASE = config.apiBase;

    const fetchBus = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/rh/get_bus.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                const busNormalises = (data.bus || []).map(b => ({
                    ...b,
                    photo: config.normalizeBusPhoto(b.photo)
                }));
                setBus(busNormalises);
            }
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBus();
    }, []);

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
                setFormData({ ...formData, photo: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const ajouterBus = async () => {
        if (!formData.nom.trim()) {
            toast.error('Nom du bus requis');
            return;
        }
        if (!formData.chauffeur_nom.trim() || !formData.chauffeur_prenom.trim()) {
            toast.error('Nom et prénom du chauffeur requis');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/rh/ajouter_bus.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Bus ajouté');
                setShowForm(false);
                setFormData({ nom: '', chauffeur_nom: '', chauffeur_prenom: '', chauffeur_telephone: '', photo: '' });
                setPhotoPreview(null);
                fetchBus();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    const changerStatut = async (id, statutActuel) => {
        const nouveauStatut = statutActuel === 'en_marche' ? 'en_panne' : 'en_marche';
        const confirmMsg = nouveauStatut === 'en_panne' ? 'Mettre ce bus en panne ?' : 'Remettre ce bus en marche ?';
        if (!window.confirm(confirmMsg)) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/rh/changer_statut_bus.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id_bus: id, statut: nouveauStatut })
            });
            const data = await response.json();
            if (data.success) {
                toast.success(`Bus ${nouveauStatut === 'en_marche' ? 'remis en marche' : 'mis en panne'}`);
                fetchBus();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const ouvrirProgrammation = (b) => {
        setBusProgramme(b);
        setBesoinHuile(false);
        setDescription('');
        setProgrammeData(true);
    };

    const envoyerProgramme = async () => {
        if (!description.trim()) {
            toast.error('Description du voyage requise');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/rh/programmer_bus.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id_bus: busProgramme.id_bus,
                    besoin_huile: besoinHuile,
                    description: description
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Programme envoyé au financier');
                setProgrammeData(null);
                setBusProgramme(null);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatutBadge = (statut) => {
        if (statut === 'en_marche') {
            return <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-semibold">✅ En marche</span>;
        }
        return <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-semibold">🔧 En panne</span>;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-6xl">
                {/* Header */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white shadow-xl">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold">🚌 Gestion des bus</h1>
                            <p className="mt-2 opacity-90">Gérez la flotte de bus et programmez les trajets</p>
                        </div>
                        <button onClick={() => setShowForm(!showForm)} className="bg-white/20 px-4 py-2 rounded-xl hover:bg-white/30 transition">
                            + Ajouter un bus
                        </button>
                    </div>
                </div>

                {/* Formulaire ajout bus */}
                {showForm && (
                    <div className="mb-6 rounded-2xl bg-white p-6 shadow-xl">
                        <h2 className="text-lg font-bold text-slate-800 mb-4">🚌 Nouveau bus</h2>
                        <div className="grid gap-4 md:grid-cols-2">
                            <input type="text" placeholder="Nom du bus" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} className="rounded-xl border border-slate-200 px-4 py-2" />
                            <input type="text" placeholder="Nom du chauffeur" value={formData.chauffeur_nom} onChange={(e) => setFormData({ ...formData, chauffeur_nom: e.target.value })} className="rounded-xl border border-slate-200 px-4 py-2" />
                            <input type="text" placeholder="Prénom du chauffeur" value={formData.chauffeur_prenom} onChange={(e) => setFormData({ ...formData, chauffeur_prenom: e.target.value })} className="rounded-xl border border-slate-200 px-4 py-2" />
                            <input type="tel" placeholder="Téléphone du chauffeur" value={formData.chauffeur_telephone} onChange={(e) => setFormData({ ...formData, chauffeur_telephone: e.target.value })} className="rounded-xl border border-slate-200 px-4 py-2" />
                            <div className="md:col-span-2">
                                <div onClick={() => fileInputRef.current?.click()} className="cursor-pointer rounded-xl border-2 border-dashed border-purple-300 p-4 text-center hover:border-purple-500">
                                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                                    {photoPreview ? <img src={photoPreview} alt="Bus" className="max-h-32 mx-auto rounded" /> : <div><div className="text-3xl mb-2">🚌</div><p className="text-slate-500">Photo du bus</p></div>}
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 flex gap-3">
                            <button onClick={ajouterBus} disabled={submitting} className="bg-purple-600 text-white px-6 py-2 rounded-xl hover:bg-purple-700">Ajouter</button>
                            <button onClick={() => { setShowForm(false); setPhotoPreview(null); }} className="bg-slate-200 px-6 py-2 rounded-xl">Annuler</button>
                        </div>
                    </div>
                )}

                {/* Liste des bus */}
                {loading ? (
                    <div className="flex justify-center py-12"><div className="h-12 w-12 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600"></div></div>
                ) : bus.length === 0 ? (
                    <div className="rounded-2xl bg-white p-12 text-center"><p className="text-slate-500">Aucun bus enregistré</p></div>
                ) : (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {bus.map(b => (
                            <div key={b.id_bus} className="rounded-2xl bg-white shadow-lg overflow-hidden">
                                <div className="relative h-40 bg-slate-200">
                                    {b.photo ? <img src={b.photo} alt={b.nom} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-6xl">🚌</div>}
                                </div>
                                <div className="p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-lg">{b.nom}</h3>
                                        {getStatutBadge(b.statut)}
                                    </div>
                                    <div className="space-y-1 text-sm">
                                        <p className="text-slate-600">👨‍✈️ Chauffeur: {b.chauffeur_prenom} {b.chauffeur_nom}</p>
                                        <p className="text-slate-600">📞 {b.chauffeur_telephone || 'Non renseigné'}</p>
                                    </div>
                                    <div className="mt-4 flex gap-2">
                                        {b.statut === 'en_marche' && (
                                            <button onClick={() => ouvrirProgrammation(b)} className="flex-1 bg-blue-600 text-white py-2 rounded-xl text-sm hover:bg-blue-700">
                                                📅 Programmer
                                            </button>
                                        )}
                                        <button onClick={() => changerStatut(b.id_bus, b.statut)} className={`flex-1 py-2 rounded-xl text-sm ${b.statut === 'en_marche' ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-green-100 text-green-600 hover:bg-green-200'}`}>
                                            {b.statut === 'en_marche' ? '🔧 Mettre en panne' : '✅ Remettre en marche'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal programmation */}
            {programmeData && busProgramme && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        <div className="border-b p-4">
                            <h3 className="text-xl font-bold">📅 Programmer {busProgramme.nom}</h3>
                        </div>
                        <div className="p-5">
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">🛢️ Besoin d'huile ?</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2"><input type="radio" checked={besoinHuile === true} onChange={() => setBesoinHuile(true)} /> Oui</label>
                                    <label className="flex items-center gap-2"><input type="radio" checked={besoinHuile === false} onChange={() => setBesoinHuile(false)} /> Non</label>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">📝 Description du voyage</label>
                                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="4" className="w-full rounded-xl border border-slate-200 px-4 py-2" placeholder="Destination, horaires, remarques..." />
                            </div>
                            <div className="text-sm text-slate-500 mb-4 p-3 bg-slate-50 rounded-lg">
                                <p>👨‍✈️ Chauffeur: {busProgramme.chauffeur_prenom} {busProgramme.chauffeur_nom}</p>
                                <p>📞 {busProgramme.chauffeur_telephone || 'Non renseigné'}</p>
                            </div>
                        </div>
                        <div className="border-t p-4 flex gap-3">
                            <button onClick={envoyerProgramme} disabled={submitting} className="flex-1 bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700">Envoyer</button>
                            <button onClick={() => setProgrammeData(null)} className="bg-slate-200 px-4 py-2 rounded-xl">Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}